import { Button, Field } from '@freightapi/ui'
import { createFileRoute, Link } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import { ApiError, signUp } from '../lib/api'
import { useConsoleI18n } from '../lib/locale'

/**
 * Open signup (ADR 0005): a name, a company name and a work address. The organisation, the owner
 * membership and the first `sk_test_` key are created by the API once the link is opened, never
 * here — the browser only ever asks for an e-mail to be sent.
 */
export const Route = createFileRoute('/signup')({ component: Signup })

interface Form {
  name: string
  organizationName: string
  email: string
}

const EMPTY: Form = { name: '', organizationName: '', email: '' }

function Signup() {
  const { t, locale } = useConsoleI18n()
  const [form, setForm] = useState<Form>(EMPTY)
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [error, setError] = useState<string | undefined>(undefined)
  const [fieldErrors, setFieldErrors] = useState<Readonly<Record<string, string>>>({})

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setState('sending')
    setError(undefined)
    setFieldErrors({})
    try {
      await signUp({ ...form, locale })
      setState('sent')
    } catch (cause) {
      setState('idle')
      if (cause instanceof ApiError && cause.fieldErrors.length > 0) {
        setFieldErrors(
          Object.fromEntries(cause.fieldErrors.map((field) => [field.path, field.message])),
        )
        return
      }
      setError(t(cause instanceof ApiError ? cause.messageKey : 'error.unexpected'))
    }
  }

  if (state === 'sent') {
    return (
      <section className="mx-auto flex max-w-md flex-col gap-3">
        <h1 className="font-semibold text-display text-ink">{t('signup.sent.title')}</h1>
        <p className="text-body text-muted">{t('signup.sent.body', { email: form.email })}</p>
      </section>
    )
  }

  return (
    <section className="mx-auto flex max-w-md flex-col gap-5">
      <h1 className="font-semibold text-display text-ink">{t('signup.title')}</h1>
      <p className="text-body text-muted">{t('signup.intro')}</p>

      <form className="flex flex-col gap-4" onSubmit={(event) => void onSubmit(event)}>
        <TextInput
          label={t('signup.name.label')}
          name="name"
          autoComplete="name"
          value={form.name}
          error={fieldErrors.name}
          onChange={(name) => setForm((previous) => ({ ...previous, name }))}
        />
        <TextInput
          label={t('signup.organization.label')}
          name="organization_name"
          autoComplete="organization"
          value={form.organizationName}
          error={fieldErrors.organization_name}
          onChange={(organizationName) =>
            setForm((previous) => ({ ...previous, organizationName }))
          }
        />
        <TextInput
          label={t('signup.email.label')}
          name="email"
          type="email"
          autoComplete="email"
          value={form.email}
          error={fieldErrors.email ?? error}
          onChange={(email) => setForm((previous) => ({ ...previous, email }))}
        />
        <Button type="submit" block busy={state === 'sending'} busyLabel={t('loading')}>
          {t('signup.submit')}
        </Button>
      </form>

      <Link to="/login" className="text-body text-brand-700 underline">
        {t('signup.to_login')}
      </Link>
    </section>
  )
}

function TextInput({
  label,
  name,
  type = 'text',
  autoComplete,
  value,
  error,
  onChange,
}: {
  readonly label: string
  readonly name: string
  readonly type?: 'text' | 'email'
  readonly autoComplete: string
  readonly value: string
  readonly error: string | undefined
  readonly onChange: (value: string) => void
}) {
  return (
    <Field label={label} required error={error}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          type={type}
          name={name}
          autoComplete={autoComplete}
          required
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="min-h-touch rounded-card border border-line bg-surface px-3 text-body text-ink"
        />
      )}
    </Field>
  )
}
